<template>
  <!-- A full-screen edge vignette that glows in time with the momentum multiplier, so the whole
       screen feels more charged the higher the chain climbs. Pointer-events none: purely ambient.
       Teleported to <body> so position:fixed anchors to the real viewport: mounted inside the game
       tree, a transformed/filtered ancestor would otherwise contain it and clip the top and bottom
       edges (notably on iOS). -->
  <Teleport to="body">
    <Transition name="aura-fade">
      <div v-if="multiplier > 1" class="momentum-aura" :style="auraVars" aria-hidden="true"></div>
    </Transition>
  </Teleport>
</template>

<script setup>
import {computed} from "vue";
import {MOMENTUM_TIERS} from "../composables/gameConstants.js";

const props = defineProps({
  multiplier: {type: Number, default: 1},
});

// 0 (×1) .. 4 (×3). MOMENTUM_TIERS is highest-first, so invert the index.
const tier = computed(() => {
  const idx = MOMENTUM_TIERS.findIndex(t => props.multiplier === t.multiplier);
  return idx === -1 ? 0 : MOMENTUM_TIERS.length - 1 - idx;
});

// Each tier: a bigger, brighter, hotter glow that pulses faster than the last.
const auraVars = computed(() => {
  const t = [
    {size: 0, speed: 2.4, color: "transparent"},
    {size: 40, speed: 2.2, color: "rgba(45, 212, 191, 0.16)"},
    {size: 58, speed: 1.9, color: "rgba(74, 222, 128, 0.2)"},
    {size: 80, speed: 1.5, color: "rgba(250, 204, 21, 0.25)"},
    {size: 110, speed: 1.1, color: "rgba(249, 115, 22, 0.32)"},
  ][tier.value];
  return {
    "--aura-size": `${t.size}px`,
    "--aura-color": t.color,
    "--aura-speed": `${t.speed}s`,
  };
});
</script>

<style scoped>
.momentum-aura {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 30;
  animation: aura-pulse var(--aura-speed, 1.6s) ease-in-out infinite;
}

/* Inset glow concentrated at the screen edges; the blur and spread breathe with the tier speed. */
@keyframes aura-pulse {
  0%, 100% {
    box-shadow: inset 0 0 calc(var(--aura-size) * 0.7) calc(var(--aura-size) * 0.14) var(--aura-color);
  }
  50% {
    box-shadow: inset 0 0 var(--aura-size) calc(var(--aura-size) * 0.24) var(--aura-color);
  }
}

/* Ease the aura in and out so it doesn't snap on a tier change or a reset. */
.aura-fade-enter-active,
.aura-fade-leave-active {
  transition: opacity 0.4s ease;
}

.aura-fade-enter-from,
.aura-fade-leave-to {
  opacity: 0;
}
</style>
